import { useCallback, useRef } from 'react';
import { t } from '@lingui/core/macro';
import { Box, IconButton, ScrollArea, Tooltip, Typography, pxToRem } from '@pocketlaw/tetris';
import { Falcon } from '@pocketlaw/tetris/macro';
import { useDraggableWidth } from './useDraggableWidth';
import { FLOATING_SIDEBAR_CSS_VARIABLE_WIDTH, FloatingSidebarBody, FloatingSidebarContainer, FloatingSidebarDraggableHover, FloatingSidebarHeader, SidebarDraggableLine, } from './styled';
/**
 * A floating version of the screen layout `Sidebar`.
 *
 * Unlike `Sidebar` it floats on top of the content instead of pushing it, and its open state is
 * controlled by the consumer instead of by the shared screen layout sidebar state. Render it with
 * the `floatingSidebar` prop of `ScreenLayout.Root` to get it positioned inside the layout.
 */
export function FloatingSidebar(props) {
    const { open, title, actions, onClose, scrollable = true, initialWidth = 'narrow', children } = props;
    const containerRef = useRef(null);
    const setWidth = useCallback((width) => {
        var _a;
        (_a = containerRef.current) === null || _a === void 0 ? void 0 : _a.style.setProperty(FLOATING_SIDEBAR_CSS_VARIABLE_WIDTH, pxToRem(width));
    }, []);
    const { dragging, onDraggableMouseDown } = useDraggableWidth(setWidth);
    const closeLabel = t({
        message: 'Close sidebar',
        comment: 'Floating sidebar - Close sidebar button label',
    });
    return (<FloatingSidebarContainer ref={containerRef} $open={open} $initialWidth={initialWidth}>
      <FloatingSidebarHeader>
        <Typography $fontSize="small" $fontWeight="semibold" $appearance="300" $truncate>
          {title}
        </Typography>

        <Box gap="sm" alignItems="center">
          {actions}

          <IconButton appearance="ghost" size="xs" aria-label={closeLabel} onClick={onClose}>
            <Falcon icon="xmark"/>
          </IconButton>
        </Box>
      </FloatingSidebarHeader>

      <FloatingSidebarBody>{scrollable ? <ScrollArea>{children}</ScrollArea> : children}</FloatingSidebarBody>

      <Tooltip side="left" delay={1000} disabled={dragging} title={t({
            message: 'Drag to resize',
            comment: 'Sidebar - Resize tooltip label',
        })}>
        <FloatingSidebarDraggableHover $isDragging={dragging} onMouseDown={onDraggableMouseDown}>
          <SidebarDraggableLine />
        </FloatingSidebarDraggableHover>
      </Tooltip>
    </FloatingSidebarContainer>);
}
